import { FocusEvent } from 'react';

import { ElementType, Track } from '@1debit/analytics';
import { datadogRum } from '@datadog/browser-rum';
import once from 'lodash/once';

import { AutofillEvent } from 'chime-ui/components/TextField/TextField';

import { getAnalyticsHandler } from 'utils/analytics/eventRouter';
import { PageEvent } from 'utils/analytics/events';
import { RfaError, trackRfaError } from 'utils/analytics/rfaEvents';
import { requestFailureContext } from 'utils/graphql/requestFailureContext';

function buildIntegrationsObject(integrationsDecisions) {
  return integrationsDecisions?.shouldSendToFacebook
    ? {}
    : {
        integrations: {
          'Facebook Pixel': false,
          'Facebook Conversions API': false,
          'Facebook Offline Conversions': false,
        },
      };
}

function track(eventName, eventProperties, integrationsDecisions = {}) {
  const integrations = buildIntegrationsObject(integrationsDecisions);

  analytics.track(eventName, eventProperties, integrations);
}

// Fires when a PAv2 receiver non-member visits the Landing Page (First Page) of the PAv2 Transfer
export const trackLandingPageViewed = (amount, publicId, senderId) => {
  track('Landing Page Viewed', {
    amount: amount,
    public_id: publicId,
    sender_id: senderId,
  });
  getAnalyticsHandler().trackPage(PageEvent.PAY_ANYONE_LANDING_PAGE, {
    page_details: {
      [PageEvent.PAY_ANYONE_LANDING_PAGE]: {
        amount,
        sender_id: senderId,
      },
    },
    item_id: publicId,
  });
};

export const trackValuePropsViewed = (pageLocation, publicId, position) => {
  track('Value Props Viewed', {
    public_id: publicId,
    page_location: pageLocation,
    position: position,
  });
  // Note: WebItemViewed.item_type is a constrained ItemType enum (not free-form),
  // so 'payanyone_public_id' cannot be set here — item_id alone carries the public_id.
  getAnalyticsHandler().trackEvent(Track.WebItemViewed, {
    unique_id: 'pay_anyone_value_props_viewed',
    element_type: ElementType.ELEMENT_TYPE_SECTION,
    item_value: String(position).toLowerCase(),
    item_id: publicId,
  });
};

// Fires when a PAv2 receiver non-member visits the Return User Page (First Page) of the PAv2 Transfer
export const trackReturnUserPageViewed = (amount, publicId, senderId) => {
  track('Return User Page Viewed', {
    amount: amount,
    public_id: publicId,
    sender_id: senderId,
  });
  getAnalyticsHandler().trackPage(PageEvent.PAY_ANYONE_RETURN_USER_PAGE, {
    page_details: {
      [PageEvent.PAY_ANYONE_RETURN_USER_PAGE]: {
        amount,
        sender_id: senderId,
      },
    },
    item_id: publicId,
  });
};

// Fires when a PAv2 receiver non-member visits the unified Claim Info Page (Second Page) of the PAv2 Transfer
export const trackPayAnyoneClaimInfoPageViewed = publicId => {
  trackClaimInfoPageViewed(publicId);
  getAnalyticsHandler().trackPage(PageEvent.PAY_ANYONE_CLAIM_INFO_PAGE, {
    item_id: publicId,
  });
};

// Fires when a PAv2 receiver non-member visits the unified Claim Info Page (Second Page) of the PAv2 Transfer
const trackClaimInfoPageViewed = publicId => {
  track('Pay Anyone Claim Info Page Viewed', {
    public_id: publicId,
  });
};

// Fires when a PAv2 receiver non-member visits the Confirmation Page (Fourth Page) of the PAv2 Transfer
export const trackPayAnyoneConfirmationInfoPageViewed = publicId => {
  track('Pay Anyone Confirmation Page Viewed', {
    public_id: publicId,
  });
  getAnalyticsHandler().trackPage(PageEvent.PAY_ANYONE_CONFIRMATION_PAGE, {
    item_id: publicId,
  });
};

// Fires whenever a receiver taps any CTA button in the Transfer flow,
// including checking boxes on 'Debit Card Info Page' for terms & conditions
export const trackCTATapped = (
  label, // text on CTA, example: 'Instantly transfer for free', 'Next'
  pageLocation, // specific page, example: 'Landing Page' , 'Personal Info Page'
  uniqueId, // what the button is, example: 'pay_anyone_next_button'
  publicId,
  claimOption = 'N/A'
) => {
  track(
    `Pay Anyone CTA Tapped`,
    {
      label: label,
      page_location: pageLocation,
      unique_id: uniqueId,
      public_id: publicId,
      claim_option: claimOption,
    },
    { shouldSendToFacebook: true }
  );
  // Note: item_type is preserved for claimOption (existing tracking) rather than
  // repurposed for 'payanyone_public_id' — item_id alone conveys the public_id link.
  // Coerce null to 'N/A' because some call sites (e.g. Header login button) pass
  // null explicitly, which bypasses the default parameter value and sends null to
  // Event Router — which is stricter than Segment about the string|undefined type.
  getAnalyticsHandler().trackEvent(Track.WebItemClicked, {
    unique_id: 'pay_anyone_cta_tapped',
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
    label,
    item_type: claimOption ?? 'N/A',
    item_value: uniqueId,
    item_id: publicId,
  });
};

// Fires whenever a PAv2 receiver types in a field and focuses out of the field to the next
// field or taps the next available CTA button in the PAv2 Transfer flow
export const trackFieldFilled = (
  fieldLabel, // what the field is, example: 'First name'
  pageLocation, // specific page, example: 'Personal Info Page'
  value, // what was typed in, example: 'Annie'
  publicId
) => {
  track(`Pay Anyone Field Filled`, {
    field_label: fieldLabel,
    page_location: pageLocation,
    value: value,
    public_id: publicId,
  });
  getAnalyticsHandler().trackEvent(Track.WebFieldFilled, {
    unique_id: 'pay_anyone_field_filled',
    label: fieldLabel,
    // WebFieldFilled.value is typed string in the schema; coerce so booleans
    // (e.g. "Save Debit Card" checkbox) and numbers don't fail validation.
    value: String(value),
    item_id: publicId,
  });
};

// Fires whenever a PAv2 receiver submits the debit card info form successfully
export const trackTermsAccepted = (termsValue, pageLocation, publicId) => {
  track('Pay Anyone Terms Accepted', {
    terms_accepted: termsValue,
    page_location: pageLocation,
    public_id: publicId,
  });
  getAnalyticsHandler().trackEvent(Track.WebFieldFilled, {
    unique_id: 'pay_anyone_terms_accepted',
    label: 'terms_accepted',
    value: String(termsValue),
    item_id: publicId,
  });
};

// Fires whenever any type of error/failure occurs in the PAv2 Transfer flow
export const trackPayAnyoneReceiverError = (
  pageLocation, // specific page, example: 'Personal Info Page'
  errorMessage, // what the error display to receiver is
  publicId,
  failure? // the query error behind the screen, if any
) => {
  const error = new Error('Pay Anyone Receiver Error');
  track('Pay Anyone Receiver Error', {
    page_location: pageLocation,
    error_message: errorMessage,
    error_type: 'frontend',
    public_id: publicId,
  });
  getAnalyticsHandler().trackEvent(Track.WebErrorOccurred, {
    unique_id: 'pay_anyone_receiver_error',
    error_code: 'frontend',
    error_message: errorMessage,
    item_id: publicId,
  });

  datadogRum.addError(error, {
    publicId,
    errorMessage,
    pageLocation,
    ...(failure && requestFailureContext(failure)),
  });
};

// An RFA read failed and the guest sees the connection error screen.
export const trackRfaLookupError = (operationName: string, failure) => {
  datadogRum.addError(new Error('RFA Lookup Error'), {
    operationName,
    ...requestFailureContext(failure),
  });
  trackRfaError(RfaError.LOOKUP, operationName);
};

export const trackApplePayButtonLoadTimeout = (timeoutMs: number) => {
  datadogRum.addError(new Error('apple_pay_button_load_timeout'), {
    timeoutMs,
  });
};

type EventType = FocusEvent & {
  detail: {
    autoFilled: boolean;
  };
};

export type InputFieldTrackingFunction = (
  event: FocusEvent | AutofillEvent,
  publicId?: string
) => void;

export const handleTrackingInputs = (
  fn: InputFieldTrackingFunction
): InputFieldTrackingFunction =>
  once((event: EventType, publicId: string) => {
    if (
      event.type === 'blur' ||
      (event.type === 'autofill' && event.detail.autoFilled === true)
    ) {
      fn(event, publicId);
    }
  });

// Fire segment track event for successful OCR Scan
export const trackSuccessfulOcrScan = (publicId, isPartialScan) => {
  datadogRum.addAction('OCR Successful Scan', { publicId, isPartialScan });

  track('OCR Scanned', {
    public_id: publicId,
    is_partial: isPartialScan,
  });
  getAnalyticsHandler().trackEvent(Track.WebActionTriggered, {
    unique_id: 'pay_anyone_ocr_scanned',
    item_id: publicId,
    item_details: {
      pay_anyone_ocr_scanned: {
        is_partial: isPartialScan,
      },
    },
  });
};

// Fire segment track event for OCR not supported
export const trackOcrNotTriggered = (publicId, reason) => {
  track('OCR Not Triggered', {
    public_id: publicId,
    reason,
  });
  getAnalyticsHandler().trackEvent(Track.WebActionTriggered, {
    unique_id: 'pay_anyone_ocr_not_triggered',
    item_id: publicId,
    item_details: {
      pay_anyone_ocr_not_triggered: {
        reason,
      },
    },
  });
};

// Fire segment track event for OCR triggered
export const trackOcrTriggered = publicId => {
  datadogRum.addAction('OCR Triggered', { publicId });
  track('OCR Triggered', {
    public_id: publicId,
  });
  getAnalyticsHandler().trackEvent(Track.WebActionTriggered, {
    unique_id: 'pay_anyone_ocr_triggered',
    item_id: publicId,
  });
};

// Fire segment track event for OCR Recognizer error
export const trackOcrRecognizerError = (publicId, reason) => {
  track('Recognizer Error Event', {
    public_id: publicId,
    reason,
  });
  getAnalyticsHandler().trackEvent(Track.WebErrorOccurred, {
    unique_id: 'pay_anyone_ocr_recognizer_error',
    error_message: reason,
    item_id: publicId,
  });
};

// Fire segment track event for P2PEnrollEmail accessed
export const trackP2PEnrollEmailLinkAccessed = (
  publicId,
  receiverIdentifier
) => {
  track('P2P Enroll Email Link Accessed Event', {
    public_id: publicId,
    receiver_identifier: receiverIdentifier,
  });
  getAnalyticsHandler().trackEvent(Track.WebActionTriggered, {
    unique_id: 'p2p_enroll_email_link_accessed',
    item_id: publicId,
    item_details: {
      p2p_enroll_email_link_accessed: {
        receiver_identifier: receiverIdentifier,
      },
    },
  });
};

export function getAnonymousId(): string | undefined {
  const analytics = (globalThis as any).analytics;

  if (!analytics?.user) return undefined;

  try {
    return analytics.user()?.anonymousId?.();
  } catch {
    return undefined;
  }
}
